"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as HelpCircle, Phone, ShieldCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "What accreditations does BMF Scaffolding hold?",
  answer: "BMF Scaffolding Ltd holds NASC and CHAS accreditations."
}, {
  id: "item-2",
  question: "What areas does BMF cover?",
  answer: "Berkshire, Surrey, Hampshire, Buckinghamshire, Greater London and the wider South East and Home Counties."
}, {
  id: "item-3",
  question: "Do you work with main contractors and developers?",
  answer: "Yes, BMF partners with main contractors, developers, roofing contractors, solar installers, councils and facilities managers, delivering site-specific RAMS and NASC/CHAS-compliant scaffolding."
}, {
  id: "item-4",
  question: "How experienced is the BMF team?",
  answer: "BMF is led by Pete Wood, who brings more than 20 years of hands-on scaffolding experience, continuing the legacy established by Adam Story."
}, {
  id: "item-5",
  question: "How do I request a quote?",
  answer: "Via the Request a Quote page, or by calling 01344 929900."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faqaccordion" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-1.5 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-card text-foreground border-border">
              <HelpCircle className="size-3.5 text-primary mr-1" />
              Common questions
            </Badge>
          </div>
          <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Frequently asked questions
          </h1>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Straight answers about our scaffolding accreditations, regional coverage, team background, and quote process.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="mt-12 sm:mt-16">
          <div className="rounded-xl border border-border bg-card p-4 sm:p-6 md:p-8 shadow-sm">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
              {faqItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="rounded-lg border border-border/80 bg-background px-4 py-1 transition-[background-color,border-color] duration-200 hover:border-border">
                  <AccordionTrigger className="font-default text-base font-semibold text-foreground hover:no-underline py-4 text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4 pt-1">
                    {item.id === "item-5" ? <div>
                        <p>{item.answer}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <Button asChild size="sm" variant="default" className="shadow-xs">
                            <Link to="/ContactRequestQuotePage">
                              Request a quote
                              <ArrowRight className="size-3.5 ml-1" />
                            </Link>
                          </Button>
                          <a href="tel:01344929900" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                            <Phone className="size-3.5" />
                            01344 929900
                          </a>
                        </div>
                      </div> : <p>{item.answer}</p>}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-10 rounded-xl border border-border bg-muted/40 p-6 sm:p-8 text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="size-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                NASC & CHAS Accredited Contractor
              </span>
            </div>
            <h3 className="font-default text-lg font-semibold text-foreground">
              Ready to discuss your project requirements?
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Get site-specific RAMS and fully compliant scaffolding solutions.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}